import { useState, useEffect, useRef } from 'react'
import { Plus, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react'
import clientesService from '../../services/clientesService'

function VendedorRow({ item, isPrincipal, mostrarPercentual, onSelecionarVendedor, onChangePercentual, onRemove, podeRemover, disabled }) {
  const [search, setSearch] = useState(item.vendedor_nome || '')
  const [opcoes, setOpcoes] = useState([])
  const debounceRef = useRef(null)

  useEffect(() => {
    setSearch(item.vendedor_nome || '')
  }, [item.vendedor_id, item.vendedor_nome])

  const buscar = (termo) => {
    setSearch(termo)
    clearTimeout(debounceRef.current)
    if (!termo || termo.length < 2) { setOpcoes([]); return }
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await clientesService.getByOrganizador(item.organizadorId, 1, 20, termo)
        setOpcoes(Array.isArray(data) ? data : (data?.data || []))
      } catch (e) { console.error(e) }
    }, 300)
  }

  const selecionar = (cliente) => {
    onSelecionarVendedor({ vendedor_id: cliente.id, vendedor_nome: cliente.nome })
    setSearch(cliente.nome)
    setOpcoes([])
  }

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <input
          type="text" value={search} disabled={disabled}
          onChange={(e) => buscar(e.target.value)}
          onBlur={() => setTimeout(() => setOpcoes([]), 150)}
          className={`input-rematei text-sm ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`} placeholder="Digite o nome do vendedor..."
        />
        {opcoes.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {opcoes.map(c => (
              <li key={c.id} onMouseDown={() => selecionar(c)} className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer">
                {c.nome}
              </li>
            ))}
          </ul>
        )}
      </div>
      {mostrarPercentual && (
        <div className="relative w-24 flex-shrink-0">
          <input
            type="number" min="0" max="100" step="0.01" disabled={disabled}
            value={item.percentual}
            onChange={(e) => onChangePercentual(e.target.value)}
            className={`input-rematei text-sm pr-6 ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            title={isPrincipal ? 'Ajusta automaticamente conforme a % dos outros vendedores' : undefined}
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">%</span>
        </div>
      )}
      {podeRemover && !disabled && (
        <button type="button" onClick={onRemove} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Remover vendedor">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

/**
 * Lista editável de vendedores/donos de um lote (fluxo cavalo — lote pode ter
 * N vendedores, cada um com sua % de participação na venda).
 * Controlado: recebe `vendedores` e notifica mudanças via `onChange`.
 */
export default function VendedoresCavaloEditor({ vendedores, onChange, organizadorId, disabled }) {
  const lista = vendedores.map(v => ({ ...v, organizadorId }))
  const soma = lista.reduce((s, v) => s + (parseFloat(v.percentual) || 0), 0)

  const adicionar = () => {
    onChange([...vendedores, { vendedor_id: '', vendedor_nome: '', percentual: '' }])
  }

  const remover = (index) => {
    onChange(vendedores.filter((_, i) => i !== index))
  }

  const atualizarVendedorSelecionado = (index, dados) => {
    onChange(vendedores.map((v, i) => i === index ? { ...v, ...dados } : v))
  }

  // Ao editar a % de um vendedor secundário (índice > 0), o vendedor principal
  // (índice 0) absorve automaticamente o restante pra fechar 100% — evita ter que
  // ficar recalculando manualmente toda vez que ajusta um dos outros.
  const atualizarPercentual = (index, valor) => {
    let novos = vendedores.map((v, i) => i === index ? { ...v, percentual: valor } : v)
    if (index !== 0 && novos.length > 1) {
      const somaOutros = novos.slice(1).reduce((s, v) => s + (parseFloat(v.percentual) || 0), 0)
      const restante = Math.max(0, Math.round((100 - somaOutros) * 100) / 100)
      novos = novos.map((v, i) => i === 0 ? { ...v, percentual: restante } : v)
    }
    onChange(novos)
  }

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Vendedor(es) *</label>
      <p className="text-xs text-gray-500 -mt-2">Vendedor(es) e participação de cada um na venda</p>
      {lista.map((item, index) => (
        <VendedorRow
          key={index}
          item={item}
          isPrincipal={index === 0 && vendedores.length > 1}
          mostrarPercentual={vendedores.length > 1}
          onSelecionarVendedor={(dados) => atualizarVendedorSelecionado(index, dados)}
          onChangePercentual={(valor) => atualizarPercentual(index, valor)}
          onRemove={() => remover(index)}
          podeRemover={vendedores.length > 1}
          disabled={disabled}
        />
      ))}

      <div className="flex items-center justify-between">
        {!disabled && (
          <button
            type="button"
            onClick={adicionar}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            <Plus className="w-4 h-4" /> Adicionar Vendedor
          </button>
        )}

        {vendedores.length > 1 && (
          <span className={`flex items-center gap-1 text-xs font-medium ${
            Math.abs(soma - 100) < 0.01 ? 'text-green-600' : 'text-amber-600'
          }`}>
            {Math.abs(soma - 100) < 0.01
              ? <CheckCircle2 className="w-3.5 h-3.5" />
              : <AlertTriangle className="w-3.5 h-3.5" />}
            Total: {soma.toFixed(2)}%
          </span>
        )}
      </div>
    </div>
  )
}
